import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, CheckCircle, Loader2, ArrowLeft, Eye, EyeOff, Check, X } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { apiService } from '@/services/api';
import { getApiErrorMessage } from '@/lib/apiErrors';
import {
  resetPasswordFormSchema,
  type ResetPasswordFormData,
  STANDARD_PASSWORD_REQUIREMENTS,
  checkPasswordRequirements,
} from '@/lib/passwordValidation';

export const ResetPassword: React.FC = () => {
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const navigate = useNavigate();
  const searchParams = useSearch({ from: '/reset-password' }) as { token?: string };
  const token = searchParams.token;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError: setFormError,
    clearErrors,
  } = useForm<ResetPasswordFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    shouldFocusError: false,
    shouldUseNativeValidation: false,
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const newPassword = watch('newPassword', '');
  const passwordRequirements = checkPasswordRequirements(newPassword, STANDARD_PASSWORD_REQUIREMENTS);

  // If no token is provided, show error state
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <Logo />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Invalid reset link</h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              This password reset link is invalid or has expired
            </p>
          </div>

          <Card className="border-gray-200 dark:border-gray-700">
            <CardContent className="space-y-4 pt-6">
              <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                <AlertCircle size={16} />
                The password reset link you clicked is invalid or has expired.
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Please request a new password reset link to continue.
              </p>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Link
                to="/forgot-password"
                className="w-full"
              >
                <Button className="w-full">
                  Request new reset link
                </Button>
              </Link>
              <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                <Link
                  to="/login"
                  className="inline-flex items-center font-medium text-blue-600 hover:text-blue-500"
                >
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  Back to sign in
                </Link>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  const onSubmit = async (rawData: Record<string, unknown>) => {
    try {
      setIsSubmitting(true);
      setError('');
      setSuccess('');
      clearErrors();

      // Manually validate with Zod to catch and handle errors properly
      const validationResult = resetPasswordFormSchema.safeParse(rawData);
      
      if (!validationResult.success) {
        // Handle validation errors manually without throwing
        validationResult.error.issues.forEach((err) => {
          if (err.path[0] === 'newPassword') {
            setFormError('newPassword', { type: 'manual', message: err.message });
          } else if (err.path[0] === 'confirmPassword') {
            setFormError('confirmPassword', { type: 'manual', message: err.message });
          }
        });
        return; // Stop execution without throwing
      }

      const data = validationResult.data;
      const response = await apiService.resetPassword({
        token,
        newPassword: data.newPassword,
      });
      
      if (response.success) {
        setSuccess(response.message || 'Your password has been reset successfully. You can now sign in with your new password.');
        // Redirect to login after a short delay
        setTimeout(() => {
          navigate({ to: '/login' });
        }, 3000);
      } else {
        setError(getApiErrorMessage(response, 'Invalid or expired reset token. Please request a new password reset.'));
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onError = (errors: Record<string, unknown>) => {
    // This should not be called now since we handle validation manually
    if (import.meta.env.DEV) {
      console.log('Form submission errors (should not happen):', errors);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <Logo />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Reset your password</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Enter your new password to complete the reset process
          </p>
        </div>

        <Card className="border-gray-200 dark:border-gray-700">
          <CardHeader>
            <CardTitle>Set new password</CardTitle>
            <CardDescription>
              Choose a strong password for your account
            </CardDescription>
          </CardHeader>

          {success ? (
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 p-3 text-sm text-green-600 bg-green-50 border border-green-200 rounded-md">
                <CheckCircle size={16} />
                {success}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Redirecting you to the login page...
              </p>
            </CardContent>
          ) : (
            <form onSubmit={handleSubmit(onSubmit, onError)} noValidate>
              <CardContent className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                    <AlertCircle size={16} />
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="newPassword">New password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your new password"
                      {...register('newPassword')}
                      className={`pr-10 ${errors.newPassword ? 'border-red-500' : ''}`}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.newPassword && (
                    <p className="text-sm text-red-600">{errors.newPassword.message}</p>
                  )}
                </div>

                {/* Password Requirements */}
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-800/50">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Password requirements:
                  </p>
                  <ul className="space-y-1">
                    {passwordRequirements.map((req) => (
                      <li
                        key={req.id}
                        className={`flex items-center gap-2 text-xs ${
                          req.met
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {req.met ? (
                          <Check className="h-3 w-3 flex-shrink-0" />
                        ) : (
                          <X className="h-3 w-3 flex-shrink-0" />
                        )}
                        {req.label}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your new password"
                      {...register('confirmPassword')}
                      className={`pr-10 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-sm text-red-600">{errors.confirmPassword.message}</p>
                  )}
                </div>
              </CardContent>

              <CardFooter className="flex flex-col space-y-4">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting password...
                    </>
                  ) : (
                    'Reset password'
                  )}
                </Button>
              </CardFooter>
            </form>
          )}

          <CardFooter className="pt-0">
            <div className="text-center text-sm text-gray-600 dark:text-gray-400 w-full">
              <Link
                to="/login"
                className="inline-flex items-center font-medium text-blue-600 hover:text-blue-500"
              >
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back to sign in
              </Link>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};